import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Combobox,
  Group,
  Input,
  InputBase,
  Loader,
  ScrollArea,
  Stack,
  Text,
  useCombobox,
} from "@mantine/core";
import { IconCheck, IconSearch } from "@tabler/icons-react";

import { ConsultationSelectProps } from "@components/ConsultationSelect";

import { ConsultationServiceComboboxItem } from "@customTypes/index";

import { StyledInputBase } from "../ConsultationSelect/ConsultationSelect.styled";


function SelectOption({ label, category }: ConsultationServiceComboboxItem) {
  return (
    <Stack gap={5}>
      <Text fz={14}>{label}</Text>
      <Text fz={12} c="dimmed" tt={"capitalize"}>
        {category || "N/A"}
      </Text>
    </Stack>
  );
}

interface ConsultationServiceSelectProps
  extends ConsultationSelectProps<
    ConsultationServiceComboboxItem | null,
    ConsultationServiceComboboxItem
  > {
  data: ConsultationServiceComboboxItem[] | [];
  required?: boolean;
  value: ConsultationServiceComboboxItem | null;
  setValue: React.Dispatch<
    React.SetStateAction<ConsultationServiceComboboxItem | null>
  >;
}

export const ConsultationServiceSelect = ({
  placeholder,
  label,
  value,
  setValue,
  data,
  disabled,
  isLoading = false,
  required = false,
}: ConsultationServiceSelectProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch("");
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const selectedOption = data?.find(item => item.value === value?.value);

  const options = data
    ?.filter(item =>
      item.label.toLowerCase().includes(search.toLowerCase().trim())
    )
    .map(item => (
      <Combobox.Option
        value={item.value}
        key={item.value}
        active={item.value === value?.value}>
        <Group gap="xs" justify="space-between">
          <SelectOption {...item} />
          {item.value === value?.value && (
            <IconCheck size={16} color={"var(--mantine-color-black)"} />
          )}
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={val => {
        setValue(data.find(item => item.value === val) ?? null); // Potential bottle-neck
        combobox.closeDropdown();
      }}>
      <Combobox.Target>
        <StyledInputBase>
          <InputBase
            component="button"
            type="button"
            disabled={disabled}
            label={label}
            pointer
            required={required}
            rightSection={
              isLoading ? (
                <Loader size={16} color="accent.4" />
              ) : (
                <Combobox.Chevron />
              )
            }
            onClick={() => combobox.toggleDropdown()}
            rightSectionPointerEvents="none"
            multiline>
            {selectedOption ? (
              <SelectOption {...selectedOption} />
            ) : (
              <Input.Placeholder>{placeholder}</Input.Placeholder>
            )}
          </InputBase>
        </StyledInputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          placeholder={t("search-for-consultation-service")}
        />
        <Combobox.Options>
          <ScrollArea.Autosize mah={200} type="auto" scrollbarSize={7}>
            {options.length > 0 ? (
              options
            ) : (
              <Combobox.Empty>{t("nothing-found")}</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
